<template>
  <expandable-box-node v-model="opened" title="Gina" questionkey="GINA">
    <div class="d-flex flex-column gap-6">
      <basic-question
        v-model="modelValue.daytimeSymptoms"
        kind="radio"
        title="Sintomas diurnos > 2 vezes por semana?"
        :questionData="{ options: { Sim: 1, Não: 0 } }"
        :disabled="disabled"
      />
      <basic-question
        v-model="modelValue.nightAwakenings"
        kind="radio"
        title="Despertares noturnos por asma?"
        :questionData="{ options: { Sim: 1, Não: 0 } }"
        :disabled="disabled"
      />
      <basic-question
        v-model="modelValue.rescueMedicineUsage"
        kind="radio"
        title="SABA ou CI + formoterol para alívio dos sintomas > 2 vezes por semana?"
        :questionData="{ options: { Sim: 1, Não: 0 } }"
        :disabled="disabled"
      />
      <basic-question
        v-model="modelValue.limitedActivities"
        kind="radio"
        title="Limitação das atividades por asma?"
        :questionData="{ options: { Sim: 1, Não: 0 } }"
        :disabled="disabled"
      />

      <score-board :score="numericScore" :labels="scoreLabels" :showScore="false" />
    </div>
  </expandable-box-node>
</template>

<script>
import { WatchOptionsWithHandler, PropOptions } from 'vue'
import { cloneDeep } from 'lodash'
import { createNamespacedHelpers } from 'vuex'
import ScoreBoard from '../partial/Score/ScoreBoard.vue'
import ExpandableBoxNode from '../../ExpandableBox/ExpandableBoxNode.vue'
import BasicQuestion from '@/components/NewAttendanceForm/Questions/Basic/BasicQuestion.vue'

const { mapActions: newAttendanceFormActions, mapMutations: newAttendanceFormMutations } =
  createNamespacedHelpers('newAttendanceForm')

/**
 * @typedef {Object} ModelValue
 * @property {number?} daytimeSymptoms
 * @property {number?} nightAwakenings
 * @property {number?} rescueMedicineUsage
 * @property {number?} limitedActivities
 */

export default {
  components: {
    ScoreBoard,
    ExpandableBoxNode,
    BasicQuestion,
  },
  props: {
    /** @type {PropOptions<boolean>} */
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    /** @typedef {ModelValue} */
    const modelValue = {
      daytimeSymptoms: null,
      nightAwakenings: null,
      rescueMedicineUsage: null,
      limitedActivities: null,
    }

    const scoreLabels = [
      {
        text: 'Se respondeu não para todos: Asma controlada',
        highlightFn: (score) => score == 0,
        highlightColor: '#73B71E',
      },
      {
        text: 'Se respondeu "sim" em 1 ou 2 perguntas: Asma parcialmente controlada',
        highlightFn: (score) => score == 1 || score == 2,
        highlightColor: '#B2921D',
      },
      {
        text: 'Se respondeu "sim" em 3 ou 4 perguntas: Asma não controlada',
        highlightFn: (score) => score == 3 || score == 4,
        highlightColor: '#B3261E',
      },
    ]

    return {
      modelValue,
      scoreLabels,
      opened: false,
    }
  },
  computed: {
    /** @type {Computed<number?>} */
    numericScore() {
      const values = Object.values(this.modelValue).filter((value) => value !== null)
      if (values.length < 4) {
        return null
      }
      return values.reduce((acc, curr) => acc + curr, 0)
    },
  },
  methods: {
    ...newAttendanceFormActions(['getParameter']),
    ...newAttendanceFormMutations(['updateParameter']),
    parseScore() {
      if (this.numericScore == 0) {
        return 'Asma controlada'
      }

      if (this.numericScore == 1 || this.numericScore == 2) {
        return 'Asma parcialmente controlada'
      }

      if (this.numericScore == 3 || this.numericScore == 4) {
        return 'Asma não controlada'
      }

      return null
    },
    openQuestion() {
      this.opened = true
    },
  },
  watch: {
    /** @type {WatchOptionsWithHandler<ModelValue>} */
    modelValue: {
      handler(value) {
        this.updateParameter({
          parameter: 'gina',
          value: { score: this.numericScore, label: this.parseScore(this.numericScore), questions: value },
        })
      },
      deep: true,
    },
  },
  async created() {
    const { questions } = (await this.getParameter('gina')) ?? {}
    if (questions) {
      this.modelValue = {
        ...this.modelValue,
        ...cloneDeep(questions),
      }
    }

    this.$eventBus.on(`open-question-GINA`, this.openQuestion)
    this.$eventBus.emit(`question-GINA-loaded`)
  },
  beforeDestroy() {
    this.$eventBus.off(`open-question-GINA`, this.openQuestion)
  },
}
</script>

<style></style>
